import { useEffect } from "react";
import { Link } from "react-router-dom";
import BlogLayout from "../components/BlogLayout";

export default function ShopifyWebDev() {
  useEffect(() => {
    document.title =
      "Shopify & Web Development for Ecommerce Brands | Evolve Media Agency";
    const link =
      document.querySelector("link[rel='canonical']") ||
      document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute(
      "href",
      "https://evolvemediaagency.com/shopify-web-development"
    );
    if (!document.querySelector("link[rel='canonical']"))
      document.head.appendChild(link);

    const meta =
      document.querySelector("meta[name='description']") ||
      document.createElement("meta");
    meta.setAttribute("name", "description");
    meta.setAttribute(
      "content",
      "Shopify stores, landing pages, and conversion-led web development for ecommerce brands. Content-first design, SEO architecture, and speed optimization to lower CAC and lift CVR."
    );
    if (!document.querySelector("meta[name='description']"))
      document.head.appendChild(meta);

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Shopify & Web Development",
      provider: {
        "@type": "Organization",
        name: "Evolve Media Agency",
        url: "https://evolvemediaagency.com",
      },
      areaServed: "Worldwide",
      description:
        "Shopify stores, landing pages, sales funnels, and brand websites built around the brand's own photo, video, and copy — engineered for CTR, CVR, and SEO.",
      url: "https://evolvemediaagency.com/shopify-web-development",
    });
    document.head.appendChild(jsonLd);
    window.scrollTo(0, 0);
    return () => {
      jsonLd.remove();
    };
  }, []);

  return (
    <BlogLayout>
      <div style={{ fontFamily: "Montserrat, sans-serif", color: "#1a1a1a" }}>
        <div
          style={{
            background: "#000",
            padding: "64px 24px 48px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: "800px", margin: "0 auto" }}>
            <p
              style={{
                color: "#01c61f",
                fontWeight: 800,
                fontSize: "13px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                margin: "0 0 16px",
              }}
            >
              SERVICE
            </p>
            <h1
              style={{
                color: "#fff",
                fontWeight: 900,
                fontSize: "clamp(2rem, 5vw, 3rem)",
                lineHeight: 1.15,
                margin: "0 0 20px",
              }}
            >
              Shopify & Web Development for Ecommerce Brands That Want to Convert
            </h1>
            <p
              style={{
                color: "#aaa",
                fontSize: "1.1rem",
                maxWidth: "600px",
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              Stores, landing pages, and sales funnels engineered around the two
              metrics that drive ecommerce revenue: CTR and CVR.
            </p>
          </div>
        </div>

        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
            padding: "48px 24px 80px",
          }}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: `
              <p style="font-size:1.15rem;color:#444;line-height:1.8;margin-bottom:24px;">Most ecommerce websites are built backwards. A template gets picked, the dev team plugs in placeholder copy, the brand drops in stock photos, and then — almost as an afterthought — someone wonders why the conversion rate is stuck at 1.4%.</p>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:32px;">We build the other way around. <strong>The content drives the site.</strong> The photos, videos, copy, and offers come first. The structure exists to deliver them at the right moment in the buyer's decision. The result is a Shopify store that performs like a sales asset instead of a brochure.</p>

              <div style="background:#f7f7f7;border-left:4px solid #01c61f;border-radius:8px;padding:20px 24px;margin:32px 0;">
                <p style="font-weight:700;font-size:1rem;margin:0 0 8px;color:#1a1a1a;">📋 What's on this page</p>
                <ul style="margin:0;padding-left:20px;color:#444;line-height:2;">
                  <li>The two metrics that decide whether your site makes money</li>
                  <li>What we build (Shopify, landing pages, funnels, brand sites)</li>
                  <li>Why content-led design beats template agencies</li>
                  <li>SEO-first architecture and the lowest-CAC channel</li>
                  <li>How site + ad management compounds ROAS</li>
                </ul>
              </div>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">📈 The Two Metrics That Drive Everything: CTR and CVR</h2>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">Every ecommerce decision — design, copy, layout, image choice, page speed — should ladder up to one of two metrics. <strong>Click-through rate (CTR)</strong> determines how much traffic your ads, organic listings, and emails actually deliver to the site. <strong>Conversion rate (CVR)</strong> determines how much of that traffic turns into revenue.</p>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">A template store with stock photos and generic copy might convert at 1.2%. A content-led custom build with original imagery, video, and proof elements regularly hits 3–5% on the same product. That's the entire difference between a brand that scales profitably and one that subsidizes Meta and Google ad spend forever.</p>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">🛠 What We Build</h2>

              <ul style="list-style:none;padding:0;margin:16px 0 28px;">
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">🛍</span>
                  <div><strong style="color:#1a1a1a;">Shopify stores</strong> — Custom themes built on Shopify 2.0, optimized for Core Web Vitals and conversion. Theme architecture designed around <em>your</em> product, not someone else's.</div>
                </li>
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">🎯</span>
                  <div><strong style="color:#1a1a1a;">High-converting landing pages</strong> — Single-purpose pages for paid traffic, launches, and offers. Built to test fast and iterate weekly.</div>
                </li>
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">🪜</span>
                  <div><strong style="color:#1a1a1a;">Sales funnels</strong> — Pre-sell pages, advertorials, upsell flows, and post-purchase offers that lift AOV and LTV without extra ad spend.</div>
                </li>
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">📦</span>
                  <div><strong style="color:#1a1a1a;">Product detail pages (PDPs)</strong> — The single highest-leverage page on any ecommerce site. We rebuild PDPs around proof, video, and clear value prop.</div>
                </li>
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;border-bottom:1px solid #f0f0f0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">🌐</span>
                  <div><strong style="color:#1a1a1a;">Brand websites</strong> — Story-led sites for brands that need to communicate values, mission, and differentiation beyond the product.</div>
                </li>
                <li style="display:flex;align-items:flex-start;gap:12px;padding:14px 0;">
                  <span style="font-size:1.4rem;flex-shrink:0;">⚡</span>
                  <div><strong style="color:#1a1a1a;">Speed & Core Web Vitals optimization</strong> — Slow sites tank both CVR and SEO. We audit and rebuild for sub-2s loads on mobile.</div>
                </li>
              </ul>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">🎨 Why Content-Led Design Beats Template Agencies</h2>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">Most web agencies hand you a Figma, build the design, and then tell you to "supply the assets." The result is predictable: stock photos, AI-generated headers, and copy that reads like every competitor in the category.</p>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">We make the photo, video, and copy in-house — the same team building your product visuals also builds the site that displays them. That means the hero video was shot for the hero placement. The lifestyle imagery was directed for the collection page. The copy was written by someone who studied your category. Nothing is borrowed and nothing fights itself.</p>

              <div style="background:#fff9ed;border:2px solid #ffb800;border-radius:8px;padding:20px 24px;margin:32px 0;">
                <p style="font-weight:700;font-size:1.05rem;margin:0 0 8px;color:#1a1a1a;">💡 The compounding effect</p>
                <p style="font-size:1rem;color:#444;margin:0;line-height:1.7;">A photo shoot we run for your Shopify hero also fuels Amazon main images, Meta ads, and email creative. One content investment → six channels. Template agencies can't do that because they don't make the content.</p>
              </div>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">🔍 SEO-First Architecture: The Lowest-CAC Channel You Already Own</h2>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">Paid traffic gets more expensive every quarter. Organic traffic compounds. A site built with proper schema, fast load times, semantic HTML, and a smart content/category structure can earn rankings that pay back every month for years — at a customer-acquisition cost approaching zero.</p>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">Most Shopify themes are SEO disasters by default — bloated DOM, broken canonicals, duplicate collection URLs, missing structured data. We rebuild the foundation so the site is technically clean before a single content piece ships, then we build out a content layer (blog, guides, comparison pages) that actually ranks.</p>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">📣 Site + Ad Management = Compounding ROAS</h2>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">A great site with bad ads underperforms. Great ads pointing at a bad site burn cash. The brands that scale fastest treat the site and ads as one system: ad creative, landing page, offer, and post-purchase flow all designed together.</p>

              <p style="font-size:1.05rem;color:#444;line-height:1.8;margin-bottom:20px;">When the same team owns the content, site, and ad creative, every winning piece of creative gets reflected on the site within a week — not a quarter. That feedback loop is where 2x ROAS becomes 4x ROAS.</p>

              <h2 style="font-size:1.75rem;font-weight:800;margin:48px 0 16px;color:#1a1a1a;border-bottom:2px solid #f0f0f0;padding-bottom:12px;">🔗 Pair this with the rest of the stack</h2>

              <ul style="list-style:none;padding:0;margin:16px 0 28px;">
                <li style="padding:10px 0;border-bottom:1px solid #f0f0f0;">
                  <a href="/product-photos" style="color:#01c61f;font-weight:700;text-decoration:none;font-size:1.05rem;">📸 Product Photography Portfolio →</a>
                  <p style="margin:4px 0 0;color:#888;font-size:.9rem;">The same photo team shoots assets for both your Shopify PDPs and Amazon listings.</p>
                </li>
                <li style="padding:10px 0;border-bottom:1px solid #f0f0f0;">
                  <a href="/video-portfolio" style="color:#01c61f;font-weight:700;text-decoration:none;font-size:1.05rem;">🎬 Video Portfolio →</a>
                  <p style="margin:4px 0 0;color:#888;font-size:.9rem;">Hero videos and product videos engineered for site, ads, and Amazon — one shoot, every channel.</p>
                </li>
                <li style="padding:10px 0;">
                  <a href="/ecommerce-content-roi" style="color:#01c61f;font-weight:700;text-decoration:none;font-size:1.05rem;">💰 The ROI of Professional Ecommerce Content →</a>
                  <p style="margin:4px 0 0;color:#888;font-size:.9rem;">The math on why content quality decides whether your site scales or stalls.</p>
                </li>
              </ul>

              <div style="background:#000;border-radius:16px;padding:40px;margin-top:64px;text-align:center;color:#fff;">
                <p style="color:#01c61f;font-weight:800;font-size:13px;letter-spacing:2px;text-transform:uppercase;margin:0 0 12px;">BUILD A SITE THAT CONVERTS</p>
                <h3 style="font-weight:900;font-size:1.6rem;margin:0 0 16px;">Get a Free Site &amp; Funnel Audit</h3>
                <p style="color:#aaa;font-size:1rem;max-width:480px;margin:0 auto 28px;line-height:1.7;">
                  We'll review your current Shopify store or product page and show you exactly where conversion is leaking — plus what we'd build to fix it.
                </p>
                <div style="display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">
                  <a href="https://calendly.com/evolvemedia/amz-30-min" target="_blank" rel="noopener noreferrer"
                    style="background:#01c61f;color:#fff;padding:14px 28px;border-radius:9999px;font-weight:700;font-size:15px;text-decoration:none;">
                    Book a Free Site Audit
                  </a>
                  <a href="https://evolveamz.com/web-development/" target="_blank" rel="noopener noreferrer"
                    style="background:#6aa7f0;color:#fff;padding:14px 28px;border-radius:9999px;font-weight:700;font-size:15px;text-decoration:none;">
                    Full Web Dev Service →
                  </a>
                </div>
                <p style="color:#777;font-size:.85rem;margin:20px 0 0;">
                  Want the full agency-level breakdown? See the complete web development service page on
                  <a href="https://evolveamz.com/web-development/" target="_blank" rel="noopener noreferrer" style="color:#01c61f;text-decoration:underline;margin-left:4px;">EvolveAMZ.com</a>.
                </p>
              </div>
            `,
            }}
            style={{ lineHeight: 1.8, fontSize: "1.05rem" }}
          />
        </div>
      </div>
    </BlogLayout>
  );
}
